import React, { useState, useRef, useEffect, useMemo } from 'react';
import { getSidebarRoutes, isRouteActive } from '../config/routeConfig';

interface SidebarProps {
  currentPath: string;
  onNavigate: (routeId: string, params?: Record<string, string>, data?: Record<string, any>) => void;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPath, onNavigate, onLogout }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['schoolSetup']));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [submenuHeights, setSubmenuHeights] = useState<Record<string, number>>({});
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Get all sidebar routes dynamically - memoize to prevent re-calculation
  const sidebarRoutes = useMemo(() => getSidebarRoutes(), []);

  // Calculate submenu heights
  useEffect(() => {
    const heights: Record<string, number> = {};
    Object.entries(submenuRefs.current).forEach(([id, ref]) => {
      if (ref) {
        heights[id] = ref.scrollHeight;
      }
    });
    setSubmenuHeights(heights);
  }, []); // Only calculate once on mount

  // Auto-expand parent if child is active
  useEffect(() => {
    const newExpanded = new Set<string>();
    
    sidebarRoutes.forEach(({ id, children }) => {
      if (children && isRouteActive(id, currentPath)) {
        newExpanded.add(id);
      }
    });
    
    // Only update if there's a change
    const currentExpandedArray = Array.from(expandedItems).sort();
    const newExpandedArray = Array.from(newExpanded).sort();
    const hasChanges = currentExpandedArray.length !== newExpandedArray.length ||
      currentExpandedArray.some((item, index) => item !== newExpandedArray[index]);
    
    if (hasChanges) {
      setExpandedItems(newExpanded);
    }
  }, [currentPath]); // Only depend on currentPath

  // Collapse all submenus when sidebar is collapsed
  useEffect(() => {
    if (isCollapsed) {
      setExpandedItems(new Set());
    }
  }, [isCollapsed]);

  const toggleExpanded = (routeId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(routeId)) {
      newExpanded.delete(routeId);
    } else {
      newExpanded.add(routeId);
    }
    setExpandedItems(newExpanded);
  };

  const handleMouseEnter = (label: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (isCollapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 10
      });
      setHoveredItem(label);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleNavigate = (routeId: string) => {
    onNavigate(routeId, {}, {});
  };

  return (
    <>
      <div style={{ 
        width: isCollapsed ? '90px' : '300px', 
        background: 'linear-gradient(180deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        padding: isCollapsed ? '24px 12px' : '24px 15px',
        color: 'white',
        minHeight: '100vh',
        boxShadow: '2px 0 12px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{ 
          marginBottom: '32px', 
          padding: '8px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          gap: '12px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: '#d4f17e',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <i className="pi pi-th-large" style={{ fontSize: '1.4rem', color: '#0f3d3e' }}></i>
          </div>
          {!isCollapsed && (
            <h2 style={{ 
              margin: 0, 
              fontSize: '1.5rem', 
              fontWeight: '700',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.5px'
            }}>
              HCI
            </h2>
          )}
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            position: 'absolute',
            top: '24px',
            right: '-14px',
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            background: '#0f3d3e',
            border: '2px solid #1a5555',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            transition: 'transform 0.2s ease',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <i 
            className={`pi ${isCollapsed ? 'pi-chevron-right' : 'pi-chevron-left'}`} 
            style={{ fontSize: '0.75rem', color: 'white' }}
          ></i>
        </button>
        
        <nav style={{ 
          flex: 1, 
          overflowY: 'auto', 
          paddingRight: '10px',
        }}>
          {sidebarRoutes.map(({ id, route, children }) => {
            const isActive = isRouteActive(id, currentPath);
            const isExpanded = expandedItems.has(id);
            const hasChildren = children && children.length > 0;

            return (
              <div key={id} style={{ marginBottom: '4px' }}>
                {/* Parent Menu Item */}
                <button
                  onClick={() => {
                    if (hasChildren && !isCollapsed) {
                      toggleExpanded(id);
                    } else if (hasChildren && isCollapsed) {
                      // When collapsed, expand sidebar and show children
                      setIsCollapsed(false);
                      setExpandedItems(new Set([id]));
                    } else {
                      handleNavigate(id);
                    }
                  }}
                  onMouseEnter={(e) => {
                    handleMouseEnter(route.label, e);
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(212, 241, 126, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    handleMouseLeave();
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: isCollapsed ? '16px 8px' : '12px 16px',
                    background: isActive && !hasChildren ? '#d4f17e' : 'transparent',
                    border: 'none',
                    borderRadius: '12px',
                    color: isActive && !hasChildren ? '#0f3d3e' : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.9rem',
                    fontWeight: isActive && !hasChildren ? '600' : '400',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : (hasChildren ? 'space-between' : 'flex-start'),
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className={`pi ${route.icon}`} style={{ 
                      marginRight: isCollapsed ? '0' : '16px', 
                      fontSize: '1.2rem' 
                    }}></i>
                    {!isCollapsed && route.label}
                  </div>
                  {hasChildren && !isCollapsed && (
                    <i 
                      className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} 
                      style={{ 
                        fontSize: '0.9rem', 
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                      }}
                    ></i>
                  )}
                </button>

                {/* Children (Submenu) */}
                {hasChildren && !isCollapsed && (
                  <div 
                    ref={(el) => { submenuRefs.current[id] = el; }}
                    style={{ 
                      marginTop: '4px',
                      marginLeft: '16px',
                      borderLeft: '2px solid rgba(212, 241, 126, 0.2)',
                      paddingLeft: '8px',
                      maxHeight: isExpanded ? `${submenuHeights[id] || 1000}px` : '0px',
                      opacity: isExpanded ? 1 : 0,
                      overflow: 'hidden',
                      transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {children.map(({ id: childId, route: childRoute }) => {
                      const isChildActive = isRouteActive(childId, currentPath);
                      
                      return (
                        <button
                          key={childId}
                          onClick={() => handleNavigate(childId)}
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            marginBottom: '4px',
                            background: isChildActive ? 'rgba(212, 241, 126, 0.2)' : 'transparent',
                            border: 'none',
                            borderRadius: '10px',
                            color: 'white',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '0.95rem',
                            fontWeight: isChildActive ? '500' : '400',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: isChildActive ? 1 : 0.85
                          }}
                          onMouseEnter={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.background = 'rgba(212, 241, 126, 0.1)';
                              e.currentTarget.style.opacity = '1';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.opacity = '0.85';
                            }
                          }}
                        >
                          {childRoute.icon && (
                            <i className={`pi ${childRoute.icon}`} style={{ marginRight: '12px', fontSize: '1rem' }}></i>
                          )}
                          {childRoute.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        
        <div style={{ 
          marginTop: 'auto',
          paddingTop: '20px',
        }}>
          <button
            onClick={onLogout}
            onMouseEnter={(e) => {
              handleMouseEnter('Logout', e);
              e.currentTarget.style.background = 'rgba(212, 241, 126, 0.1)';
            }}
            onMouseLeave={(e) => {
              handleMouseLeave();
              e.currentTarget.style.background = 'transparent';
            }}
            style={{
              width: '100%',
              padding: isCollapsed ? '16px 8px' : '16px 20px',
              background: 'transparent',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '1rem',
              fontWeight: '400',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start'
            }}
          >
            <i className="pi pi-sign-out" style={{ 
              marginRight: isCollapsed ? '0' : '16px', 
              fontSize: '1.2rem' 
            }}></i>
            {!isCollapsed && 'Logout'}
          </button>
        </div>
      </div>

      {/* Tooltip Overlay */}
      {isCollapsed && hoveredItem && (
        <div
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)',
            background: '#0f3d3e',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            animation: 'tooltipFadeIn 0.2s ease',
            border: '1px solid #1a5555'
          }}
        >
          {hoveredItem}
          <div
            style={{
              position: 'absolute',
              left: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '6px solid #0f3d3e'
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
            transform: translateY(-50%) translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(-50%) translateX(0);
          }
        }
        
        nav::-webkit-scrollbar {
          width: 6px;
        }
        
        nav::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          margin: 8px 0;
        }
        
        nav::-webkit-scrollbar-thumb {
          background: rgba(212, 241, 126, 0.3);
          border-radius: 10px;
        }
        
        nav::-webkit-scrollbar-thumb:hover {
          background: rgba(212, 241, 126, 0.4);
        }
      `}</style>
    </>
  );
};

export default Sidebar;